export interface DescriptionHistoryEntry {
  description: string;
  usageCount: number;
  lastUsed: string;
}

export class DescriptionHistoryService {
  private static readonly STORAGE_KEY = 'descriptionHistory';
  private static readonly MAX_ENTRIES = 100;

  static load(): DescriptionHistoryEntry[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private static save(entries: DescriptionHistoryEntry[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      console.error('Failed to save description history:', error);
    }
  }

  static addDescription(description: string): void {
    const trimmed = description.trim();
    if (!trimmed) return;

    let entries = this.load();
    const existing = entries.find(e => e.description === trimmed);

    if (existing) {
      existing.usageCount++;
      existing.lastUsed = new Date().toISOString();
    } else {
      entries.push({
        description: trimmed,
        usageCount: 1,
        lastUsed: new Date().toISOString()
      });
    }

    entries.sort((a, b) => {
      if (b.usageCount !== a.usageCount) {
        return b.usageCount - a.usageCount;
      }
      return a.description.localeCompare(b.description);
    });

    if (entries.length > this.MAX_ENTRIES) {
      entries = entries.slice(0, this.MAX_ENTRIES);
    }

    this.save(entries);
  }

  static getAll(): DescriptionHistoryEntry[] {
    return this.load();
  }

  static search(keyword: string): DescriptionHistoryEntry[] {
    const entries = this.load();
    if (!keyword.trim()) return entries.slice(0, 10);

    const lowerKeyword = keyword.toLowerCase();
    const filtered = entries.filter(e => 
      e.description.toLowerCase().includes(lowerKeyword)
    );

    filtered.sort((a, b) => {
      if (b.usageCount !== a.usageCount) {
        return b.usageCount - a.usageCount;
      }
      return a.description.localeCompare(b.description);
    });

    return filtered.slice(0, 10);
  }

  /**
   * Return the most recently used descriptions (by lastUsed desc).
   * If keyword is provided, only entries containing the keyword are returned.
   */
  static getRecent(limit = 3, keyword?: string): DescriptionHistoryEntry[] {
    const entries = this.load();
    const filtered = (keyword && keyword.trim())
      ? entries.filter(e => e.description.toLowerCase().includes(keyword.toLowerCase()))
      : entries;
    const sorted = [...filtered].sort((a, b) => {
      const at = Date.parse(a.lastUsed || '');
      const bt = Date.parse(b.lastUsed || '');
      return bt - at;
    });
    return sorted.slice(0, Math.max(0, limit));
  }

  static clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear description history:', error);
    }
  }
}
